import { AltosNovedades, altoNovedades, HojaNovedadesVM, paginarNovedades } from "./paginarNovedades";
import { CampoVM, GrupoNovedadesVM, PeritajeVM } from "./peritajeViewModel";

/**
 * ── Reparto del certificado en hojas ─────────────────────────────────────────
 *
 * La hoja 1 abre con la portada (resumen, fotos, diagnóstico y mediciones), que siempre cabe.
 * Todo lo demás es un flujo de bloques que se apilan en orden: cuando uno no cabe en lo que
 * queda de la hoja, sigue en la siguiente. Así ninguna hoja queda con medio alto en blanco solo
 * porque el bloque siguiente "tocaba" en otra hoja.
 *
 * Los bloques largos se parten: las novedades por filas (en dos columnas), los detalles del
 * vehículo por filas de campos y el anexo por filas de fotos. Las observaciones y la pareja
 * accesorios/valores van siempre enteras.
 */

/** Fotos por fila del anexo: la rejilla de `.pa-anexo`. */
export const FOTOS_POR_FILA = 3;

/** Campos por fila de los detalles: la rejilla de `.pa-detalles`. */
export const CAMPOS_POR_FILA = 4;

/** Un bloque del flujo, o el tramo de él que cae en una hoja. */
export type PiezaVM =
    | { tipo: "novedades"; columnas: HojaNovedadesVM; continuacion: boolean }
    | { tipo: "notas" }
    | { tipo: "columnas" }
    | { tipo: "detalles"; filas: CampoVM[][]; conTipo: boolean; continuacion: boolean }
    | { tipo: "anexo"; fotos: string[]; numero: number; total: number };

/**
 * En px de la hoja sin escalar. `marco` es lo que una tarjeta ocupa además de sus filas: franja
 * naranja, barra de título, relleno y bordes.
 */
export interface MedidasHojas {
    /** Alto del flujo en la hoja 1, debajo de la portada. */
    primera: number;
    /** Alto del flujo en las demás hojas, debajo de la cabecera. */
    resto: number;
    /** Espacio entre dos bloques seguidos. */
    separacion: number;
    notas: number;
    columnas: number;
    novedades: AltosNovedades & { marco: number };
    detalles: { marcoConTipo: number; marcoSinTipo: number; fila: number };
    anexo: { marco: number; fila: number; separacion: number };
}

/**
 * Sin layout que medir (por ejemplo al renderizar en pruebas) todo va a la hoja 1: no hay
 * forma de saber qué cabe, y así al menos no se pierde nada.
 */
export const SIN_MEDIDAS: MedidasHojas = {
    primera: Infinity,
    resto: Infinity,
    separacion: 0,
    notas: 0,
    columnas: 0,
    novedades: { marco: 0, titulo: 1, item: 1 },
    detalles: { marcoConTipo: 0, marcoSinTipo: 0, fila: 1 },
    anexo: { marco: 0, fila: 1, separacion: 0 },
};

const trocear = <T>(lista: T[], tamano: number): T[][] => {
    const trozos: T[][] = [];
    for (let i = 0; i < lista.length; i += tamano) trozos.push(lista.slice(i, i + tamano));
    return trozos;
};

/** Lo mínimo de las novedades que hay que pintar para medirlas: un título y una fila. */
const sondaNovedades = (grupos: GrupoNovedadesVM[]): HojaNovedadesVM => [
    [{ titulo: grupos[0].titulo, continua: false, items: grupos[0].items.slice(0, 1) }],
];

/**
 * Lo que se pinta en la primera pasada, antes de repartir: una muestra de cada bloque en la
 * hoja 1 (se desborda, pero nunca llega a verse) y una hoja vacía. Sobre eso se mide el alto
 * libre de las dos y lo que ocupa cada bloque.
 */
export const sondaHojas = (vm: PeritajeVM, anexo: string[]): PiezaVM[][] => {
    const muestra: PiezaVM[] = [
        { tipo: "notas" },
        { tipo: "columnas" },
        { tipo: "detalles", filas: [vm.detalles.slice(0, CAMPOS_POR_FILA)], conTipo: true, continuacion: false },
    ];
    if (vm.novedades.length > 0) {
        muestra.unshift({ tipo: "novedades", columnas: sondaNovedades(vm.novedades), continuacion: false });
    }
    if (anexo.length > 0) {
        muestra.push({ tipo: "anexo", fotos: anexo.slice(0, FOTOS_POR_FILA), numero: 1, total: 1 });
    }
    return [muestra, []];
};

/** Reparte el flujo del certificado en hojas. La primera es la parte que va bajo la portada. */
export const paginarHojas = (vm: PeritajeVM, anexo: string[], medidas: MedidasHojas): PiezaVM[][] => {
    const hojas: PiezaVM[][] = [[]];
    let libre = medidas.primera;

    const actual = () => hojas[hojas.length - 1];
    /** Hoja nueva y todavía vacía: lo que no quepa aquí no cabe en ninguna, así que va igual. */
    const enBlanco = () => hojas.length > 1 && actual().length === 0;
    /** Alto para un bloque más, descontando la separación con el anterior. */
    const disponible = () => libre - (actual().length > 0 ? medidas.separacion : 0);
    const nuevaHoja = () => {
        hojas.push([]);
        libre = medidas.resto;
    };
    const poner = (pieza: PiezaVM, alto: number) => {
        libre = disponible() - alto;
        actual().push(pieza);
    };

    const entero = (pieza: PiezaVM, alto: number) => {
        if (alto > disponible() && !enBlanco()) nuevaHoja();
        poner(pieza, alto);
    };

    /** Parte las filas en tramos, cada uno con su marco y tantas filas como quepan en su hoja. */
    const porFilas = <T>(
        filas: T[],
        medida: { marco: (primero: boolean) => number; fila: number; separacion: number },
        crear: (tramo: T[], primero: boolean) => PiezaVM,
    ) => {
        let pendientes = filas;
        let primero = true;
        while (pendientes.length > 0) {
            const marco = medida.marco(primero);
            const caben = Math.floor((disponible() - marco + medida.separacion) / (medida.fila + medida.separacion));
            if (caben < 1 && !enBlanco()) {
                nuevaHoja();
                continue;
            }
            const tramo = pendientes.slice(0, Math.max(1, caben));
            poner(crear(tramo, primero), marco + tramo.length * medida.fila + (tramo.length - 1) * medida.separacion);
            pendientes = pendientes.slice(tramo.length);
            primero = false;
            if (pendientes.length > 0) nuevaHoja();
        }
    };

    if (vm.novedades.length > 0) {
        const { marco, ...altos } = medidas.novedades;
        const partes = paginarNovedades(vm.novedades, {
            ...altos,
            primera: Math.max(0, disponible() - marco),
            resto: medidas.resto - marco,
        });
        let continuacion = false;
        partes.forEach((columnas, index) => {
            if (index > 0) nuevaHoja();
            // La parte de la hoja actual puede venir vacía: ahí no cabía ni un grupo.
            if (!columnas.some((columna) => columna.length > 0)) return;
            poner({ tipo: "novedades", columnas, continuacion }, marco + altoNovedades(columnas, altos));
            continuacion = true;
        });
    }

    entero({ tipo: "notas" }, medidas.notas);
    entero({ tipo: "columnas" }, medidas.columnas);

    const { marcoConTipo, marcoSinTipo, fila } = medidas.detalles;
    porFilas(
        trocear(vm.detalles, CAMPOS_POR_FILA),
        // El TIPO (ancho completo) solo encabeza el primer tramo.
        { marco: (primero) => (primero ? marcoConTipo : marcoSinTipo), fila, separacion: 0 },
        (filas, primero) => ({ tipo: "detalles", filas, conTipo: primero, continuacion: !primero }),
    );

    porFilas(
        trocear(anexo, FOTOS_POR_FILA),
        { marco: () => medidas.anexo.marco, fila: medidas.anexo.fila, separacion: medidas.anexo.separacion },
        (filas) => ({ tipo: "anexo", fotos: filas.flat(), numero: 0, total: 0 }),
    );

    // "ANEXO FOTOGRÁFICO 1 de 2": se numera al final, cuando ya se sabe en cuántos tramos quedó.
    const total = hojas.flat().filter((pieza) => pieza.tipo === "anexo").length;
    let numero = 0;
    return hojas.map((hoja) =>
        hoja.map((pieza) => {
            if (pieza.tipo !== "anexo") return pieza;
            numero += 1;
            return { ...pieza, numero, total };
        }),
    );
};
